import { ColumnDef } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { IoDuplicateOutline } from "react-icons/io5";

import { Badge } from "../../../../../components/badge/Badge";
import { SettingsIconButton } from "../../../../../components/button/SettingsIconButton";
import { SettingsCard } from "../../../../../components/card/SettingsCard";
import { SettingsDropdown } from "../../../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../../../components/graphics/LottieLoading";
import { DeactivateSvg } from "../../../../../components/svg/DeactivateSvg";
import { DotsSvg } from "../../../../../components/svg/DotsSvg";
import { EditPencilSvg } from "../../../../../components/svg/EditPencilSvg";
import { TrashSvg } from "../../../../../components/svg/TrashSvg";
import { Table } from "../../../../../components/table/Table";
import { TemplateModel } from "../../../../../factories/templates/models/template.model";
import { compareAlphabeticallyWithNumbersFirst } from "../../../../../utils/sort";
import { TaskTemplatesTableProps } from "../model/taskTemplatesList.types";

export const TaskTemplatesTable: React.FC<TaskTemplatesTableProps> = ({
  isLoading,
  templates,
  onEdit,
  onDeactivate,
  onActivate,
  onDuplicate,
  onDelete,
}) => {
  const templateColumns = useMemo<ColumnDef<TemplateModel>[]>(
    () => [
      {
        accessorKey: "templateName",
        header: "Templates",
        sortingFn: (rowA, rowB, columnId) =>
          compareAlphabeticallyWithNumbersFirst(
            rowA.getValue(columnId),
            rowB.getValue(columnId),
          ),
        meta: {
          className: "text-[#1D2C3B] font-semibold",
        },
      },
      {
        accessorKey: "description",
        header: "Description",
      },
      {
        id: "tasksCount",
        header: "Tasks",
        accessorFn: (row: TemplateModel) => row.workTemplateTasks.length,
        cell: ({ getValue }) => {
          const count = getValue<number>();
          return <span className={"text-gray-600"}>{count}</span>;
        },
        sortingFn: "basic",
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <Badge
            label={row.original.isActive ? "Active" : "Inactive"}
            variant={row.original.isActive ? "status-green" : "status-grey"}
          />
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Date Created",
        cell: ({ getValue }) => {
          const raw = getValue() as string;
          const date = new Date(raw);
          const formatted = date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          });

          return <span className={"text-gray-600"}>{formatted}</span>;
        },
        sortingFn: (a, b, colId) =>
          new Date(a.getValue(colId)).getTime() -
          new Date(b.getValue(colId)).getTime(),
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div
            className={"flex justify-end"}
            onClick={(event) => event.stopPropagation()}>
            <SettingsDropdown
              actionsList={[
                {
                  name: "Edit",
                  action: () => onEdit(row.original),
                  icon: <EditPencilSvg />,
                },
                {
                  name: row.original.isActive ? "Deactivate" : "Activate",
                  action: () =>
                    row.original.isActive
                      ? onDeactivate(row.original)
                      : onActivate(row.original),
                  icon: <DeactivateSvg />,
                },
                {
                  name: "Duplicate",
                  action: () => onDuplicate(row.original),
                  icon: <IoDuplicateOutline size={20} />,
                },
                {
                  name: "Delete",
                  action: () => onDelete(row.original),
                  icon: <TrashSvg />,
                  extraClasses: "text-[#DC2626]",
                  iconColor: "text-[#DC2626]",
                },
              ]}>
              <SettingsIconButton
                icon={<DotsSvg />}
                onClick={() => null}
                size={"small"}
              />
            </SettingsDropdown>
          </div>
        ),
      },
    ],
    [onActivate, onDeactivate, onDelete, onDuplicate, onEdit],
  );

  return (
    <SettingsCard>
      {isLoading ? (
        <LottieLoading />
      ) : (
        <Table
          data={templates}
          columns={templateColumns}
          initialSorting={[{ id: "templateName", desc: false }]}
          onRowClick={onEdit}
        />
      )}
    </SettingsCard>
  );
};
